import styled from 'styled-components';

/** Generic flex row used inside grid cells. Fixed to one line so content in tall
 *  (expanded) rows sticks to the first line instead of vertically centring. */
export const CellFlex = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  width: 100%;
`;

/**
 * Product-number label, rendered as a button: clicking it marks the row as the copy source.
 * `$active` highlights the current source row's label.
 */
export const CellLabel = styled.span<{ $muted?: boolean; $active?: boolean }>`
  max-width: 100%;
  border: none;
  background: transparent;
  padding: 0;
  text-align: left;
  font-family: inherit;
  font-size: 14px;
  font-weight: ${({ $active }) => ($active ? 600 : 'inherit')};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
  color: ${({ $active }) => ($active ? 'var(--bombyx-green, #159e79)' : '#3c3c3c')};
`;

/** No/Yes toggle — tinted background + coloured text, matching the Figma. */
export const YesNoPillButton = styled.button<{ $yes: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  min-width: 48px;
  height: 28px;
  padding: 0 12px;
  margin-left: 8px;
  font-size: 14px;
  line-height: normal;
  font-family: var(--font-medium), 'Aktiv Grotesk', system-ui, sans-serif;
  cursor: pointer;
  border-radius: 6px;
  color: ${({ $yes }) => ($yes ? 'var(--bombyx-green, #159e79)' : 'var(--bombiix-red, #f65158)')};
  background: ${({ $yes }) => ($yes ? 'rgba(21, 158, 121, 0.25)' : 'rgba(246, 81, 88, 0.25)')};
`;

/** Redirect-to-product-detail icon button, pinned to the right edge of the product cell. */
export const RedirectIcon = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: auto;
  padding: 0 5px 0 0;
  border: none;
  background: transparent;
  color: var(--bordercolor, #6a6a6a);
  cursor: pointer;
  line-height: 1;

  &:hover {
    color: var(--bombiix-blue, #0c97ee);
  }
`;

/** Invite Supplier icon button, sits beside a Core Product Total Price input.
 *  15px left gap matches the Figma spacing between the Total Price field and its trailing icon. */
export const InviteIcon = styled.button.attrs({ type: 'button' })<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  margin-left: 15px;
  padding: 0;
  border: none;
  background: transparent;
  color: ${({ $active }) => ($active ? 'var(--bombyx-green, #159e79)' : 'var(--bordercolor, #6a6a6a)')};
  cursor: pointer;
  line-height: 1;

  /* The global icon-font rule ([class^="icon-"]) hard-sets every glyph to #3c3c3c, overriding the
     button's colour; force the glyph to inherit so the active (green) / hover states actually show. */
  .icon-Invite {
    color: inherit;
  }

  &:not(:disabled):hover {
    color: ${({ $active }) => ($active ? 'var(--bombyx-green, #159e79)' : 'var(--bombiix-blue, #0c97ee)')};
  }

  &:disabled {
    color: var(--bordercolor, #6a6a6a);
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

/** Expand/collapse chevron button for a product row (inline SVG chevron). */
export const RowCaret = styled.button<{ $open: boolean }>`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  width: 16px;
  height: 16px;
  margin: 0 8px;
  flex: 0 0 auto;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1.5l5 5 5-5' fill='none' stroke='%236a6a6a' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  transform: rotate(${({ $open }) => ($open ? '180deg' : '0deg')});
  transition: transform 0.15s ease;
`;

/** Placeholder occupying the caret's footprint when a product has no multi-grading-group caret,
 *  so product labels stay column-aligned across rows. */
export const RowCaretSpacer = styled.span`
  width: 16px;
  height: 16px;
  margin: 0 8px;
  flex: 0 0 auto;
`;

/** Single-row group header layout: [checkbox] name [caret/icon]. */
export const GroupHeaderBox = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  height: 100%;
  font-family: var(--font-heavy);
  font-size: 14px;
  font-weight: normal;
  color: #3c3c3c;
  white-space: nowrap;
  overflow: hidden;
`;

/**
 * Product Number header layout. Mirrors CellFlex's spacing model (no flex gap —
 * the checkbox & caret carry their own margins) so the header's checkbox + caret
 * line up exactly with the ProductNumberCell below it.
 */
export const ProductHeaderBox = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 100%;
  font-family: var(--font-heavy);
  font-size: 14px;
  font-weight: normal;
  color: #3c3c3c;
  white-space: nowrap;
  overflow: hidden;
`;

export const GroupHeaderLabel = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** Small down-chevron shown beside the Product Number header label. */
export const HeaderLeadCaret = styled.span`
  width: 12px;
  height: 8px;
  flex: 0 0 auto;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1.5l5 5 5-5' fill='none' stroke='%236a6a6a' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
`;

/** Expand/collapse caret button for a column group (inline SVG chevron). */
export const HeaderCaretBtn = styled.button<{ $open: boolean }>`
  border: none;
  background-color: transparent;
  cursor: pointer;
  padding: 0;
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path d='M1 1.5l5 5 5-5' fill='none' stroke='%236a6a6a' stroke-width='1.6' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  transform: rotate(${({ $open }) => ($open ? '-90deg' : '0deg')});
  transition: transform 0.15s ease;
`;

/** Three-dot column menu glyph (inline SVG, no icon font needed). */
export const MenuDots = styled.span`
  width: 6px;
  height: 16px;
  margin-left: auto;
  flex: 0 0 auto;
  background-repeat: no-repeat;
  background-position: center;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='6' height='16' viewBox='0 0 6 16'><g fill='%236a6a6a'><circle cx='3' cy='3' r='1.4'/><circle cx='3' cy='8' r='1.4'/><circle cx='3' cy='13' r='1.4'/></g></svg>");
`;

/**
 * Three-dot column-menu button in the Product Number header (Figma "More" icon).
 * Mirrors the shared `.cag-header-menu-btn` look: 20px, rounded, hover fill.
 */
export const HeaderMenuButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 20px;
  height: 20px;
  margin-left: auto;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--bordercolor, #6a6a6a);
  cursor: pointer;

  &:hover {
    background: rgba(106, 106, 106, 0.12);
    color: #3c3c3c;
  }

  .icon-More {
    font-size: 16px;
    line-height: 1;
  }
`;

/** Muted hint shown in an expanded group header before the push button (e.g. Technical Package). */
export const GroupHeaderNote = styled.span`
  margin-left: auto;
  font-family: var(--font-book), 'Aktiv Grotesk', system-ui, sans-serif;
  font-size: 12px;
  font-weight: normal;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/** "Push / combine into one document" button on group headers (icomoon icon-Push). */
export const ExportGlyph = styled.button.attrs({ type: 'button' })`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-left: auto;
  flex: 0 0 auto;
  border: none;
  padding: 0;
  cursor: pointer;
  background-color: transparent;
  font-size: 16px;
  color: var(--bordercolor, #6a6a6a);
  line-height: 1;

  &:hover {
    background-color: rgba(106, 106, 106, 0.12);
    border-radius: 4px;
  }
`;

/** Vertical stack for grading-group lines inside a single cell. */
export const Stack = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const StackItem = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  min-height: 40px;
  width: 100%;
`;

/** Plain underlined text input used for email / quantity fields. */
export const CellInput = styled.input<{ $error?: boolean }>`
  width: 100%;
  height: 28px;
  border: 1px solid ${(props) => (props.$error ? '#fa979b' : '#D8D8D8')};
  border-radius: 4px;
  padding: 0 6px;
  font-size: 14px;
  line-height: normal;
  background: #fff;
  color: #3c3c3c;
  &::placeholder {
    color: ${(props) => (props.$error ? '#3c3c3c' : '#a4a4a4')};
    opacity: 0.7;
    font-size: 14px;
  }
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
    border-color: ${(props) => (props.$error ? '#fa979b' : '#D8D8D8')};
  }
  &:disabled {
    background: #f5f5f5;
    color: #a4a4a4;
    cursor: not-allowed;
  }
`;

/**
 * Wraps a native date input so an empty value shows a "Due Date" label instead of
 * the browser's dd/mm/yyyy placeholder. The `mssg-date-empty` class blanks the native
 * date text (webkit) so the overlaid DatePlaceholder shows through.
 */
export const DateField = styled.div`
  position: relative;
  width: 100%;

  /* Hide every native segment (dd / mm / yyyy + separators) while the value is empty
     so the "Due Date" overlay is the only visible label. Firefox has no segmented
     pseudo-elements, so the input-level color: transparent covers it there. */
  input.mssg-date-empty {
    color: transparent;
  }
  input.mssg-date-empty::-webkit-datetime-edit,
  input.mssg-date-empty::-webkit-datetime-edit-fields-wrapper,
  input.mssg-date-empty::-webkit-datetime-edit-text,
  input.mssg-date-empty::-webkit-datetime-edit-day-field,
  input.mssg-date-empty::-webkit-datetime-edit-month-field,
  input.mssg-date-empty::-webkit-datetime-edit-year-field {
    color: transparent;
  }
`;

/** "Due Date" overlay shown while the date input is empty (click-through to the input). */
export const DatePlaceholder = styled.span`
  position: absolute;
  left: 7px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 14px;
  color: var(--placeholderColor, #6a6a6a);
  opacity: 0.7;
  pointer-events: none;
`;

/** Compact native select used across grid cells (caret-styled, no menu clipping). */
export const CellSelect = styled.select<{ $muted?: boolean }>`
  width: 100%;
  min-width: 90px;
  height: 28px;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 0 22px 0 6px;
  font-size: 14px;
  line-height: normal;
  background-color: #fff;
  color: #3c3c3c;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%236a6a6a' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 7px center;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
    border-color: #d8d8d8;
  }

  &:disabled {
    background-color: #fff;
    cursor: default;
    opacity: 0.85;
  }
`;

/**
 * Combined control box — the Figma pattern where the checkbox lives INSIDE the
 * bordered control, separated from the value by a divider: [ ☐ │ value ⌄ ].
 */
export const ComboBox = styled.div<{ $error?: boolean }>`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  height: 28px;
  padding-left: 7px;
  border: 1px solid ${(props) => (props.$error ? '#fa979b' : '#D8D8D8')};
  border-radius: 4px;
  background: #fff;
  overflow: visible;

  input[type='checkbox'] {
    margin: 0;
  }

  &:hover,
  &:focus-within {
    outline: none;
    box-shadow: none;
    border-color: ${(props) => (props.$error ? '#fa979b' : '#d8d8d8')};
  }

  &:has(input:disabled) {
    background: #f5f5f5;
  }

  /* Embedded react-multi-select-component: borderless (the box supplies the border),
     sized to fit the cell. */
  .rmsc {
    flex: 1;
    min-width: 0;
    --rmsc-h: 26px;
    --rmsc-radius: 0;
    --rmsc-bg: transparent;
  }
  .rmsc .dropdown-container {
    height: 26px !important;
    min-height: 26px !important;
    border: none !important;
    border-radius: 0 !important;
    background: transparent !important;
    box-shadow: none !important;
  }
  .rmsc .dropdown-container:hover,
  .rmsc .dropdown-container:focus-within {
    border: none !important;
    box-shadow: none !important;
  }
  .rmsc .dropdown-heading {
    height: 26px !important;
    padding: 0 4px !important;
    font-family: 'Aktiv Grotesk', sans-serif;
  }
  .rmsc .dropdown-heading .dropdown-heading-value span {
    font-size: 14px !important;
    color: #3c3c3c;
  }
  .rmsc .dropdown-heading .dropdown-heading-value .gray {
    font-size: 14px !important;
    color: #a4a4a4;
  }
`;

/** Vertical divider between the checkbox and the value inside a ComboBox. */
export const ComboDivider = styled.span`
  width: 1px;
  height: 18px;
  margin: 0 7px;
  flex: 0 0 auto;
  background: rgba(106, 106, 106, 0.3);
`;

/** Borderless select that sits inside a ComboBox (the box supplies the border). */
export const BareSelect = styled.select<{ $muted?: boolean }>`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 22px 0 0;
  font-size: 14px;
  line-height: normal;
  background-color: transparent;
  color: #3c3c3c;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'><path d='M1 1l4 4 4-4' fill='none' stroke='%236a6a6a' stroke-width='1.3' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 6px center;

  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
`;

/** Plain text label that sits inside a ComboBox (non-select cells). */
export const ComboLabel = styled.span<{ $muted?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #3c3c3c;
`;

/** Inline +/delete actions that sit inside the UoM box, after the divider. */
export const UomActions = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding-right: 8px;
  flex: 0 0 auto;

  /* Sizing and colour come from master-design .bombiix-icon-container. Only the
     disabled state is local: master-design defines none, and the delete button is
     inert while a row has a single grading line. */
  .bombiix-icon-container:disabled {
    opacity: 0.4;
    cursor: default;
  }
  .bombiix-icon-container:disabled:hover {
    background: transparent;
  }
`;

/** Small "add grading line" button. */
export const AddLineButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 16px;
  color: var(--bombyx-green, #159e79);
  padding: 0;
`;

/**
 * Borderless input that sits inside a ComboBox / PrefixBox (the box supplies the border).
 * Used by the Core Product Quantity (`+ │ input`) and £-prefixed price cells.
 */
export const BareInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 100%;
  border: none;
  outline: none;
  padding: 0 6px 0 0;
  font-size: 13px;
  line-height: normal;
  background: transparent;
  color: var(--black, #000);
  &::placeholder {
    color: var(--placeholderColor, #6a6a6a);
    opacity: 0.7;
  }
  &:focus,
  &:focus-visible {
    outline: none;
    box-shadow: none;
  }
  &:disabled {
    color: #a4a4a4;
    cursor: not-allowed;
  }
`;

/**
 * Read-only display box for non-editable Costing Information fields (Supplier / Currency).
 * Matches the master-design disabled input (.new-input:disabled): #f3f3f3 fill (--pim-color-bg-header),
 * #a4a4a4 text (--pim-color-placeholder), #d8d8d8 border (--pim-color-border), not-allowed cursor.
 */
export const DisplayField = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  height: 28px;
  border: 1px solid #d8d8d8;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 13px;
  background: #f3f3f3;
  color: #a4a4a4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: not-allowed;
`;

/** Leading `£` (or other currency symbol) label inside a Core Product price PrefixBox. */
export const CurrencyPrefix = styled.span`
  flex: 0 0 auto;
  font-size: 13px;
  line-height: 1;
  color: var(--bordercolor, #6a6a6a);
`;

/**
 * Per-line radio circle for the Core Product group (trailing ○). Filled when the line is the
 * selected one for its product row. Local UI state only — business logic is a follow-on.
 */
